import React, { useState, useEffect, useCallback } from 'react';
import { X, Wallet, ArrowUpCircle, ArrowDownCircle, Loader } from 'lucide-react';
import apiService from '../../apiService';

const SOURCE_LABELS = {
  topup: 'Wallet Top-up',
  refund: 'Order Refund',
  reward: 'Reward',
  cashback: 'Cashback',
  order_payment: 'Order Payment',
  admin_credit: 'Admin Credit',
  admin_debit: 'Admin Debit',
};

const WalletTransactionsModal = ({ isOpen, onClose, walletBalance }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState('');

  const loadTransactions = useCallback(async (pageNum = 1, append = false) => {
    setLoading(true);
    setError('');
    try {
      const resp = await apiService.getWalletTransactions(pageNum);
      if (resp?.success) {
        const data = resp.data?.transactions ?? resp.data ?? {};
        const items = data.data ?? [];
        setTransactions(prev => append ? [...prev, ...items] : items);
        setHasMore(data.current_page < data.last_page);
        setPage(data.current_page ?? pageNum);
      } else {
        setError(resp?.message || 'Failed to load transactions');
      }
    } catch (e) {
      setError('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      setTransactions([]);
      setPage(1);
      loadTransactions(1, false);
    }
  }, [isOpen, loadTransactions]);

  const handleLoadMore = () => {
    const nextPage = page + 1;
    loadTransactions(nextPage, true);
  };

  if (!isOpen) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50">
      <div className="bg-white w-full max-w-md rounded-t-3xl flex flex-col" style={{ maxHeight: '85vh' }}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-2">
            <Wallet size={22} className="text-blue-600" />
            <div>
              <h2 className="text-base font-bold text-gray-800">Wallet Transactions</h2>
              <p className="text-xs text-gray-500">Balance: <span className="text-blue-600 font-semibold">₹{typeof walletBalance === 'number' ? walletBalance.toFixed(2) : '0.00'}</span></p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Transactions list */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading && transactions.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <Loader size={28} className="text-blue-600 animate-spin" />
            </div>
          ) : error ? (
            <p className="text-center text-red-500 py-8 text-sm">{error}</p>
          ) : transactions.length === 0 ? (
            <div className="text-center py-12">
              <Wallet size={40} className="text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">No transactions yet</p>
              <p className="text-gray-400 text-xs mt-1">Top up your wallet to get started</p>
            </div>
          ) : (
            <div className="space-y-3">
              {transactions.map((txn) => {
                const isCredit = txn.type === 'credit';
                const label = SOURCE_LABELS[txn.source] ?? txn.source_label ?? txn.source;
                return (
                  <div key={txn.id} className="flex items-center gap-3 bg-gray-50 rounded-xl p-3">
                    <div className={`p-2 rounded-full ${isCredit ? 'bg-green-100' : 'bg-red-100'}`}>
                      {isCredit
                        ? <ArrowUpCircle size={20} className="text-green-600" />
                        : <ArrowDownCircle size={20} className="text-red-500" />
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800 truncate">{label}</p>
                      {txn.description && (
                        <p className="text-xs text-gray-500 truncate">{txn.description}</p>
                      )}
                      <p className="text-xs text-gray-400">{formatDate(txn.created_at)}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className={`font-bold text-sm ${isCredit ? 'text-green-600' : 'text-red-500'}`}>
                        {isCredit ? '+' : '-'}₹{parseFloat(txn.amount).toFixed(2)}
                      </p>
                      <p className="text-xs text-gray-400">Bal: ₹{parseFloat(txn.balance_after).toFixed(2)}</p>
                    </div>
                  </div>
                );
              })}

              {hasMore && (
                <button
                  onClick={handleLoadMore}
                  disabled={loading}
                  className="w-full py-2.5 text-blue-600 text-sm font-medium border border-blue-200 rounded-xl hover:bg-blue-50 disabled:opacity-50 transition-colors"
                >
                  {loading ? 'Loading...' : 'Load More'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WalletTransactionsModal;
